import React from 'react'

import { CommonCardProps } from 'component-library/cards/CardItem.Props'
import {
    StyledHeroCard,
    StyledLink,
    CardTextContainer,
    StyledHeroCardMedia,
    StyledHeroCardText,
} from 'component-library/cards/HeroCard/HeroCard.styled'
import { CardByline } from 'component-library/cards/CardByline/CardByline'

import { toLinkWithHint } from 'common/routing/route-hints'
import { SponsoredLabel } from 'component-library/advertising/SponsoredLabel/SponsoredLabel'
import { KickerPosition } from '../Kicker/Kicker.styled'
import { themedValue } from 'component-library/__styling/themed-value'
import { FontScales } from 'component-library/__styling/settings/fontScale'
import { MaybeLoaded } from 'json-react-layouts-data-loader'
import { FixedRatio } from 'common/client-data-types/image-item'
import { MediaMode } from '../CardMedia/CardMedia'
import { useTheme } from '@emotion/react'
import { createCardClickedEvent } from 'common/events/collection-events'
import { PublicationCardItem } from 'common/client-data-types/card-types'

export interface HeroLayout {
    type: 'hero'
    hasBackground?: boolean
    fixedRatio?: '16:9'
    mediaMode?: MediaMode
    kickerMode?: 'hidden' | 'large' | 'default'
}

export interface Props extends CommonCardProps {
    hasBackground?: boolean
    mediaMode?: MediaMode
    imageLayout?: 'fill-container-horizontally' | 'none'
    kickerPosition?: KickerPosition
    largeKicker?: boolean
    /**
     * defaults to `4:3`
     */
    fixedRatio?: FixedRatio
    cardNumber: number
    item: MaybeLoaded<PublicationCardItem>
    heroLayout?: HeroLayout
    /**
     * HeroCard is typically first in the page so default is true
     */
    disableImageLazyLoad?: boolean
}

export const HeroCard: React.FC<Props> = props => {
    const linkClicked = (e: React.MouseEvent<HTMLElement>) => {
        if (!props.item.loaded) {
            // We don't want the link to do anything
            e.preventDefault()
            return
        }

        props.onEvent(
            createCardClickedEvent(
                props.item.result,
                'InternalHeroCard',
                'HeroCard',
                props.cardNumber
            )
        )
    }

    const theme = useTheme()
    const {
        className,
        hasBackground,
        imageLayout,
        hideKicker,
        hideByline,
        kickerPosition,
        hideTeaser,
        largeKicker,
        mediaMode,
        onEvent,
        item,
        heroLayout,
        fixedRatio,
        disableImageLazyLoad = true,
    } = props

    const showByline = theme.kind !== 'perthnow' && !hideByline
    const showSponsoredLabel = item.loaded && item.result.isSponsored
    const primaryTopic = item.loaded ? item.result.primaryTopic.id : undefined

    const fontScale = themedValue(theme, {
        thewest: 1.75,
        sevennews: 1.7,
        fallback: 2,
    }) as FontScales

    return (
        <StyledHeroCard
            className={className}
            data-topic={primaryTopic}
            isSponsored={showSponsoredLabel}
            hasBackground={hasBackground}
            primaryTopic={primaryTopic}
        >
            <StyledLink
                to={toLinkWithHint(item)}
                onClick={linkClicked}
                hasBackground={hasBackground}
                isLoading={!item.loaded}
            >
                <CardTextContainer>
                    <StyledHeroCardText
                        item={item}
                        hasBackground={hasBackground}
                        hideKicker={
                            hideKicker || (heroLayout && heroLayout.kickerMode === 'hidden')
                        }
                        hideTeaser={hideTeaser}
                        largeKicker={
                            largeKicker || (heroLayout && heroLayout.kickerMode === 'large')
                        }
                        fontScale={fontScale}
                        onEvent={onEvent}
                        kickerPosition={kickerPosition}
                    />
                    {showByline && <CardByline item={item} onEvent={onEvent} showProfile={true} />}
                    {showSponsoredLabel && <SponsoredLabel hasBackground={hasBackground} />}
                </CardTextContainer>
                {mediaMode !== 'hidden' && (
                    <StyledHeroCardMedia
                        isLarge
                        item={item}
                        fixedRatio={fixedRatio}
                        hasBackground={hasBackground}
                        imageWidths={500}
                        onEvent={onEvent}
                        className={className}
                        mediaMode={mediaMode}
                        disableImageLazyLoad={disableImageLazyLoad}
                        imageLayout={imageLayout || 'none'}
                    />
                )}
            </StyledLink>
        </StyledHeroCard>
    )
}
HeroCard.displayName = 'HeroCard'
